import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react';

import { t } from '@/i18n';
import { formatBytes, MAX_FILE_BYTES, MAX_TEXT_BYTES } from '@/lib/format';
import { looksLikePassword } from '@/lib/secretKind';

/** Photos a browser can show as a thumbnail, and up to what size it is worth decoding one. */
const PREVIEW_TYPES = /^image\/(jpeg|png|gif|webp|avif|svg\+xml)$/;
const PREVIEW_MAX_BYTES = 15 * 1024 * 1024;

const encoder = new TextEncoder();

/** The secret being written on the home screen: its text, a pasted password on its plate, an attached file and its checks. */
export function useSecretDraft(active: boolean) {
  const [text, setText] = useState('');
  // what is typed or pasted shows as dots until the eye is pressed, as a password field would
  const [masked, setMasked] = useState(true);
  // a pasted password goes on a plate at the top of the field; what is typed under it is its note
  const [password, setPassword] = useState<string | null>(null);
  const [passwordShown, setPasswordShown] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  // what goes out: the password on its first line, the note after it (lib/secretKind.ts), or just the text
  const secretText = password === null ? text : text.trim() === '' ? password : `${password}\n${text}`;
  // A small picture of a chosen photo, made on this device only (an object URL); none for big files, which would cost a phone a lot of memory.
  const preview = useMemo(
    () => (file !== null && PREVIEW_TYPES.test(file.type) && file.size <= PREVIEW_MAX_BYTES && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null),
    [file],
  );
  useEffect(
    () => () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  // A photo the browser could not draw falls back to the file icon.
  const [brokenPreview, setBrokenPreview] = useState<string | null>(null);

  const textBytes = encoder.encode(secretText).length;
  const validationError = !active ? null : textBytes > MAX_TEXT_BYTES ? t('secretDraft.textTooLarge', { max: formatBytes(MAX_TEXT_BYTES) }) : file !== null && file.size > MAX_FILE_BYTES ? t('secretDraft.fileTooLarge', { max: formatBytes(MAX_FILE_BYTES) }) : null;
  // a secret is text, a file or both
  const hasContent = active ? secretText.trim().length > 0 || file !== null : true;

  const pickFile = (event: ChangeEvent<HTMLInputElement>): void => {
    setFile(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  // a password pasted into the empty field becomes the plate; anything else is pasted as text
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
    if (password !== null || text.trim() !== '') return;
    const pasted = event.clipboardData.getData('text').trim();
    if (!looksLikePassword(pasted)) return;
    event.preventDefault();
    setPassword(pasted);
    setPasswordShown(false);
  };
  // the plate's ×: the password goes back into the field as plain text, its note after it
  const unplate = (): void => {
    if (password === null) return;
    setText(text === '' ? password : `${password}\n${text}`);
    setPassword(null);
    textarea.current?.focus({ preventScroll: true });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>, submit: () => void): void => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return {
    text,
    setText,
    masked,
    toggleMasked: () => {
      setMasked((value) => !value);
    },
    password,
    passwordShown,
    togglePasswordShown: () => {
      setPasswordShown((value) => !value);
    },
    file,
    setFile,
    secretText,
    preview,
    brokenPreview,
    setBrokenPreview,
    validationError,
    hasContent,
    textarea,
    pickFile,
    onPaste,
    unplate,
    onKeyDown,
  };
}
